import { lory } from '@rsm/allfarblori';
import h from 'hyperscript';
import generateIcon from 'components/_particles/icon/generate-icon';

export default class Slider {
  constructor($el, options) {
    this.$el = $el;

    // Classes have to be assigned to slider elements
    if (!options) {
      return;
    }

    this.options = {
      sliderControlsframemethod: 'append',
      rewind: true,
      slidesToScroll: 1,
      slidesFocusable: true,
      ...options,
    };

    // Slides
    this.$slides = this.$el.querySelectorAll(`.${this.options.sliderSlide}`);

    // Don't init slider, if only one slide exists
    if (this.$slides.length === 1) {
      return;
    }

    this.slidesTotal = this.$slides.length;

    if (this.options.slidesToScroll > 1) {
      this.slidesTotal = Math.ceil(this.$slides.length / this.options.slidesToScroll);
    }

    this.initSlider();
  }

  initClasses() {
    // Frame
    this.$sliderFrame = this.$el.querySelector(`.${this.options.sliderFrame}`);
    // Container: Collection of slides
    this.$slidesContainer = this.$el.querySelector(`.${this.options.slidesContainer}`);
    // Controls-Frame: Where to dock the controls
    this.$controlsFrame = this.$el.querySelector(`.${this.options.sliderControlsframe}`);
    this.controlsFrameMethod = this.options.sliderControlsframemethod;

    this.$slidesFrame = this.$el.querySelector('.stage-home__inner');
    this.$dotsFrame = this.$el.querySelector(`.${this.options.sliderDotsframe}`);

    this.$sliderFrame.classList.add('slider__frame');
    this.$slidesContainer.classList.add('slider__container');

    if (this.$slides) {
      this.$slides.forEach(($item) => {
        if (this.options.slidesFocusable) {
          $item.setAttribute('tabindex', '0');
        }
        $item.classList.add('slider__slide');
      });
    }
  }

  initSlider() {
    this.initClasses();

    this.$el.addEventListener('before.lory.init', () => {
      this.$slidesContainer.scrollLeft = 0;
      this.initControls();
    });

    this.$el.addEventListener('after.lory.init', () => {
      this.$el.classList.add('slider--initialized');
    });

    this.$el.addEventListener('after.lory.slide', () => {
      this.updateControls();
      this.setCaption();
      this.swipeDetect();
    });

    this.lory = lory(this.$el, {
      slidesToScroll: this.options.slidesToScroll,
      rewind: this.options.rewind,
      rewindPrev: true,
      rewindSpeed: 200,
      slideSpeed: 350,
      ease: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
      classNameFrame: this.options.sliderFrame,
      classNameSlideContainer: this.options.slidesContainer,
      classNamePrevCtrl: 'slider__control--prev',
      classNameNextCtrl: 'slider__control--next',
    });

    if (this.$slidesContainer) {
      this.$slidesContainer.addEventListener('focusin', this.focusIn);
    }
    window.addEventListener('resize', this.onResize);
    this.lory.reset();
    this.initDots();
    this.setCaption();
  }

  swipeDetect() {
    const totalItems = this.$slides.length;
    for (let i = 0; i < totalItems; i += 1) {
      this.$dotsFrame.childNodes[i].classList.remove('active');
    }
    this.$dotsFrame.childNodes[this.lory.returnIndex()].classList.add('active');
  }

  initDots() {
    this.$el.querySelector('.stage-home__dots').innerHTML = '';
    const totalItems = this.$slides.length;
    for (let i = 0; i < totalItems; i += 1) {
      if (this.$slides[i]) {
        const $thumb = h(
          'li.stage-home__dot', {
            onclick: (e) => {
              if (i < totalItems) {
                for (let j = 0; j < totalItems; j += 1) {
                  this.$dotsFrame.childNodes[j].classList.remove('active');
                }
                this.lory.slideTo(i);
                this.$dotsFrame.childNodes[i].classList.add('active');
              }
              e.preventDefault();
            },
          },
        );
        this.$el.querySelector('.stage-home__dots').appendChild($thumb);
      }
    }
    this.$dotsFrame.childNodes[0].classList.add('active');
  }

  setCaption() {
    const $activeSlide = this.$el.querySelector('.active');
    const $activeSlideCaption = $activeSlide.querySelector('figcaption');
    let caption = '';

    if ($activeSlideCaption) {
      caption = $activeSlide.querySelector('figcaption').innerHTML;
    }

    this.$el.querySelector('.stage-home__figure-caption').innerHTML = caption;
  }

  initControls() {
    if (this.$controls) {
      return;
    }

    const $controlPrev = h(
      'button.slider__control.slider__control--prev',
      generateIcon({
        icon: this.options.controlPrevIcon || 'arrow-left',
        title: 'Vorherige Inhalte',
        viewBox: this.options.controlIconViewBox || '0 0 200 200',
      }),
    );

    const $controlCount = h(
      'span.slider__count',
      h('span.slider__current', '01'),
      h('span', '/'),
      h('span.slider__total', this.slidesTotal.toString().padStart(2, '0')),
    );

    const $controlNext = h(
      'button.slider__control.slider__control--next',
      generateIcon({
        icon: this.options.controlNextIcon || 'arrow-right',
        title: 'Nächste Inhalte',
        viewBox: this.options.controlIconViewBox || '0 0 200 200',
      }),
    );

    this.$controls = h(
      '.slider__controls',
      h(
        '.slider__control-wrap.slider__control-wrap--prev', $controlPrev,
      ),
      h(
        '.slider__control-wrap.slider__control-wrap--next', $controlNext,
      ),
      $controlCount,
    );

    // Method defaults to append
    if (this.controlsFrameMethod) {
      this.$controlsFrame[this.controlsFrameMethod](this.$controls);
    }
  }

  updateControls() {
    const currentIndex = this.lory.returnIndex() + 1;
    const shownIndex = Math.ceil(currentIndex / this.options.slidesToScroll);
    if (this.$el.querySelector('.slider__current')) {
      this.$el.querySelector('.slider__current').innerHTML = shownIndex.toString().padStart(2, '0');
    }
  }

  onResize = () => {
    this.updateControls();
  }

  deconstructor() {
    const controls = this.$el.querySelectorAll('.slider__controls');
    controls.forEach(($el) => {
      $el.parentNode.removeChild($el);
    });
    this.$el.classList.remove('slider--initialized');
    this.$sliderFrame.classList.remove('slider__frame');
    this.$slidesContainer.classList.remove('slider__container');

    if (this.$slides) {
      this.$slides.forEach(($item) => {
        if (this.options.slidesFocusable) {
          $item.removeAttribute('tabindex', null);
        }
        $item.classList.remove('slider__slide');
      });
    }

    if (this.lory) {
      this.lory.destroy();
    }
  }

  focusIn = (event) => {
    if (event.target) {
      const className = this.$slides[0].classList[0];
      this.$sliderFrame.scrollLeft = 0;
      const $slide = event.target.closest(`.${className}`);
      const index = Array.prototype.indexOf.call(this.$slides, $slide);
      this.lory.slideTo(index);
    }
  }
}
